import { NavLink, useNavigate, useLocation } from 'react-router';
import { BookUser, ArrowLeftRight, House, LayoutGrid, Package, Plus, Wallet, Building2, FolderKanban, Sprout, ClipboardList, UtensilsCrossed, PiggyBank, Banknote, type LucideIcon } from 'lucide-react';
import { cn } from '@dd/ui';
import { FAB_LABEL, TAB2_LABEL, TAB4_LABEL, type Fab, type Tab2, type Tab4 } from '@dd/shared';
import { useShop } from '@/hooks/useShop';
import { isMoreInner } from './innerCtx';
import { useT } from '@/lib/i18n';
import { useSync } from '@/sync/status';

const TAB2_ICON: Record<Tab2, LucideIcon> = { baki: BookUser, cash_in_out: ArrowLeftRight, income_expense: Banknote };
const TAB4_ICON: Record<Tab4, LucideIcon> = { stock: Package, balance: Wallet, property: Building2, project: FolderKanban, crop: Sprout, order: ClipboardList, menu: UtensilsCrossed, savings: PiggyBank };
/** Where the big "+" goes, by the category's FAB (Bible 13.1). Retail, software invoices and crop sales share the sale screen; the rest have their own entry screen. */
const FAB_ROUTE: Record<Fab, string> = {
  new_sale: '/sale/new', new_cash_txn: '/mfs/txn/new', new_order: '/orders/new', new_client_or_lead: '/m/leads/new', new_invoice: '/sale/new', new_crop_sale: '/sale/new', new_order_with_measure: '/m/tailoring_orders/new', new_income_or_expense: '/personal/new',
};

/**
 * THE bottom bar (Bible 13.1): one component, identical on every main screen, driven by the category config.
 * Home · tab 2 · [FAB] · tab 4 · আরও. Brand Red marks the active tab; category colours never appear here.
 */
/** The same destinations as the bottom bar, for the desktop side menu. */
export function useNavModel() {
  const { cfg } = useShop();
  const { t, lang } = useT();
  const loc = useLocation();
  const moreActive = isMoreInner(loc.pathname);
  const items: { to: string; Icon: LucideIcon; label: string; end?: boolean; active?: boolean }[] = [
    { to: '/', Icon: House, label: t('হোম', 'Home'), end: true },
    { to: cfg.tab2 === 'baki' ? '/baki' : '/cash', Icon: TAB2_ICON[cfg.tab2], label: lang === 'bn' ? TAB2_LABEL[cfg.tab2].bn : TAB2_LABEL[cfg.tab2].en },
    { to: cfg.tab4 === 'stock' ? '/stock' : '/tab4', Icon: TAB4_ICON[cfg.tab4], label: lang === 'bn' ? TAB4_LABEL[cfg.tab4].bn : TAB4_LABEL[cfg.tab4].en },
    { to: '/more', Icon: LayoutGrid, label: t('আরও', 'More'), active: moreActive },
  ];
  const fab = { to: FAB_ROUTE[cfg.fab], label: lang === 'bn' ? FAB_LABEL[cfg.fab].bn : FAB_LABEL[cfg.fab].en };
  return { items, fab };
}

export function BottomNav() {
  const { cfg } = useShop();
  const { t, lang } = useT();
  const nav = useNavigate();
  const loc = useLocation();
  const pending = useSync((s) => s.pending);
  const T2 = TAB2_ICON[cfg.tab2];
  const T4 = TAB4_ICON[cfg.tab4];
  const moreActive = isMoreInner(loc.pathname);
  const item = (to: string, Icon: LucideIcon, label: string, end = false, badge?: number) => (
    <NavLink to={to} end={end} aria-label={label} className={({ isActive: a }) => { const isActive = to === '/more' ? moreActive || a : a; return cn('relative flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 py-1.5 text-[11px] font-semibold', isActive ? 'text-brand' : 'text-ink-3'); }}>
      {({ isActive: a }) => {
        const isActive = to === '/more' ? moreActive || a : a;
        return (
        <>
          <span className={cn('flex h-7 w-12 items-center justify-center rounded-full transition-colors', isActive && 'bg-brand-light')}><Icon size={22} strokeWidth={isActive ? 2.4 : 2} /></span>
          <span className="max-w-full truncate px-0.5">{label}</span>
          {badge ? <span className="absolute right-3 top-0.5 rounded-full bg-bad px-1.5 text-[10px] font-bold text-white">{badge}</span> : null}
        </>
        );
      }}
    </NavLink>
  );
  const fabLabel = FAB_LABEL[cfg.fab];
  return (
    <nav aria-label={t('প্রধান মেনু', 'Main menu')} className="safe-bottom fixed inset-x-0 bottom-0 z-40 border-t border-line bg-white lg:hidden">
      <div className="mx-auto grid max-w-xl grid-cols-5 items-end md:max-w-3xl">
        {item('/', House, t('হোম', 'Home'), true)}
        {item(cfg.tab2 === 'baki' ? '/baki' : '/cash', T2, lang === 'bn' ? TAB2_LABEL[cfg.tab2].bn : TAB2_LABEL[cfg.tab2].en)}
        <div className="relative flex flex-col items-center justify-end pb-1.5">
          {/* the label is part of the button: tapping the words under the "+" works too */}
          <button type="button" onClick={() => nav(FAB_ROUTE[cfg.fab], { state: { from: loc.pathname } })} aria-label={lang === 'bn' ? fabLabel.bn : fabLabel.en} className="group flex max-w-full flex-col items-center">
            <span className="-mt-7 flex h-14 w-14 items-center justify-center rounded-full bg-brand text-white shadow-float ring-4 ring-white transition-transform group-active:scale-95"><Plus size={30} strokeWidth={2.6} /></span>
            <span className="mt-0.5 max-w-full truncate px-0.5 text-[11px] font-bold text-brand">{lang === 'bn' ? fabLabel.bn : fabLabel.en}</span>
          </button>
        </div>
        {item(cfg.tab4 === 'stock' ? '/stock' : '/tab4', T4, lang === 'bn' ? TAB4_LABEL[cfg.tab4].bn : TAB4_LABEL[cfg.tab4].en)}
        {item('/more', LayoutGrid, t('আরও', 'More'), false, undefined)}
      </div>
      <span className="sr-only" aria-live="polite">{pending > 0 ? `${pending} pending` : ''}</span>
    </nav>
  );
}
